package devxertech.com.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import devxertech.com.pages.dashboard.footerSection.HomepageFooter
import devxertech.com.section.SideBar
import devxertech.com.section.TopBar
import devxertech.com.seo.PageMetadata
import devxertech.com.theme.toSitePalette
import devxertech.com.utils.Constants
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text

@Page("/privacy-policy")
@Composable
fun PrivacyPolicyPage() {
    PageMetadata(
        "Privacy Policy | DevxerTech",
        "Learn how DevxerTech handles information submitted through its website, business inquiries and career applications, and how to contact us about privacy."
    )
    val breakpoint = rememberBreakpoint()
    val palette = ColorMode.current.toSitePalette()
    var menuOpen by remember { mutableStateOf(false) }

    SideBar(breakpoint = breakpoint, isOpen = menuOpen, onClose = { menuOpen = false }, showExtraText = true)
    Column(Modifier.fillMaxWidth().backgroundColor(palette.bodyBackgroundSecondary.rgb)) {
        TopBar(breakpoint = breakpoint, onMenuClick = { menuOpen = true }, careerPageShadow = true, alwaysHovered = true)
        Section(attrs = Modifier.styleModifier {
            property("box-sizing", "border-box")
            property("width", "100%")
            property("max-width", "880px")
            property("margin", "0 auto")
            property("padding", "150px 24px 64px")
            property("font-family", Constants.FONT_FAMILY)
            property("font-size", "16px")
            property("line-height", "1.75")
            property("overflow-wrap", "anywhere")
            property("color", palette.commonSecondaryButtonColor.rgb.toString())
        }.toAttrs()) {
            H1(attrs = { style { property("font-size", "clamp(30px, 5vw, 44px)"); property("line-height", "1.2") } }) {
                Text("Privacy Policy")
            }
            P { Text("Last updated: October 13, 2026") }
            PolicySection("About this policy",
                "This policy explains how Devxer Technologies handles personal information in connection with devxertech.com, including business inquiries and career applications. It does not replace any separate agreement or privacy notice provided for a client project.")
            PolicySection("Information you provide",
                "Contact inquiries submit your name, email address, phone number with the selected country’s dialing code, subject and message. The country name is not submitted. Career applications include contact details, the position applied for and your résumé, which may contain qualifications, experience and other information you choose to provide.",
                "Please avoid including passwords, payment information or sensitive personal information in general inquiry forms.")
            PolicySection("Technical information",
                "Our website and service providers may process technical information needed to deliver and protect the website, such as your IP address, browser information, requested pages and request timestamps. The information collected depends on the hosting and services used.")
            PolicySection("How information is used",
                "We use submitted information to respond to inquiries, discuss project requirements, communicate about our services and review career applications. Technical information may be used to operate the website, investigate faults and protect against spam or misuse.")
            PolicySection("Service providers",
                "Website operation involves services for hosting, email delivery, spam protection, blog content and media delivery. These providers may process information needed to perform their functions. Loading third-party resources may disclose technical information, such as your IP address, to the provider.")
            PolicySection("Cookies and browser storage",
                "Website features and third-party services may use cookies or browser storage. You can manage these through your browser settings, although blocking them may affect some functionality. Any analytics or marketing tools must be described here when their actual configuration is confirmed.")
            PolicySection("Retention",
                "How long information is retained depends on why it was collected, whether an inquiry or application remains active, and any applicable recordkeeping obligations. Contact us for information about retention relating to your submission.")
            PolicySection("International processing",
                "Devxer operates from Pakistan. Depending on the service providers involved, information may also be processed in other countries.")
            PolicySection("Your requests",
                "You can contact us to ask about information you submitted or request access, correction or deletion. Available rights and any exceptions depend on applicable law. We may need to verify your identity before acting on a request.")
            PolicySection("External websites",
                "Links to other websites are provided for convenience. Those websites have their own privacy practices and policies.")
            PolicySection("Changes",
                "We may revise this policy when website features or information-handling practices change. The updated version will appear on this page with its revision date.")
            Section {
                PolicyHeading("Contact")
                P { Text("For privacy questions or requests:") }
                P {
                    A(href = "mailto:info@devxertech.com") { Text("info@devxertech.com") }
                }
                P { Text("Devxer Technologies") }
                P { Text("101-P, Gulberg II, Lahore, Punjab, Pakistan") }
            }
        }
        HomepageFooter(breakpoint)
    }
}

@Composable
private fun PolicyHeading(text: String) {
    H2(attrs = { style { property("font-size", "24px"); property("line-height", "1.3"); property("margin-top", "32px") } }) {
        Text(text)
    }
}

@Composable
private fun PolicySection(heading: String, vararg paragraphs: String) {
    Section {
        PolicyHeading(heading)
        paragraphs.forEach { paragraph -> P { Text(paragraph) } }
    }
}
